import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Card, Chip, DChip, DataTable, EmptyState, Modal, Pane, PaneHead, PhoneScreen, PickerChip, TabBar,
} from '@/components/kit';
import type { TableRow } from '@/components/kit';
import { useShellChrome, useNarrowViewport } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useAuthStore } from '@/stores/useAuthStore';
import { Permission } from '@/types/user';
import {
  dayMonth,
  orderMoney,
  useOrderActions,
  useOrders,
  useSuppliers,
  useWriteOff,
  type Order,
  type OrderChannel,
  type OrderLine,
  type OrderStatus,
} from './lib/useStock';
import { StockMenu } from './StockMenu';

/**
 * Stock · Orders — `g_ord_draft`, `g_ord_sent`, `g_ord_conf`, `g_ord_rec`
 * (f5, f5b, f5c, f5d). ONE SCREEN, FOUR STATES.
 *
 * The four boards are the same table with a different tab selected, and the
 * pane and the primary change with it — because the question changes: a draft
 * asks "is this right, and shall I send it?", an ordered order asks "why has
 * nobody confirmed?", a confirmed one asks "has it arrived?", and a received
 * one is closed and asks nothing.
 *
 * MAIE ASSEMBLES, THE HOLDER APPROVES. A draft arrives with an argument for
 * every line — "3 left against a minimum of 8", or the expiry lane's case that
 * the stock is there but will not survive — and both are the state the shelf
 * was in when the draft was raised, not what it is now. Editing a quantity
 * makes the line the holder's, and says so.
 *
 * ORDERED AGES VISIBLY, like an unpaid claim. Four days without a confirmation
 * is the whole reason that state exists as its own tab.
 */

const TABS: Array<{ key: OrderStatus; label: string }> = [
  { key: 'draft', label: 'Drafts' },
  { key: 'ordered', label: 'Ordered' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'received', label: 'Received' },
];

const CHANNELS: Array<{ value: OrderChannel; label: string; hint?: string }> = [
  { value: 'whatsapp', label: 'WhatsApp', hint: 'the way most suppliers are actually asked' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'manual', label: 'In person' },
];

const CHANNEL_LABEL: Record<string, string> = {
  whatsapp: 'WhatsApp',
  email: 'Email',
  phone: 'Phone',
  manual: 'In person',
};

const NONE: Order[] = [];

export function StockOrdersPage() {
  const go = useNavigate();
  const roleCheck = useAuthStore((s) => s.roleCheck);
  const canWrite = roleCheck.hasPermission(Permission.STOCK_WRITE);

  const { data, isLoading, isError } = useOrders(canWrite);
  const orders = data ?? NONE;
  const { data: suppliers } = useSuppliers();
  const act = useOrderActions();

  const [tab, setTab] = useState<OrderStatus>('draft');
  const [query, setQuery] = useState('');
  const [supplierFilter, setSupplier] = useState('');
  const [pickedId, setPicked] = useState<string | null>(null);
  const [sending, setSending] = useState<Order | null>(null);
  const [editing, setEditing] = useState<OrderLine | null>(null);
  const [returning, setReturning] = useState<Order | null>(null);
  const giveBack = useWriteOff();

  usePageMeta({ title: 'Orders' });

  const counts = useMemo(() => {
    const out: Partial<Record<OrderStatus, number>> = {};
    for (const o of orders) out[o.status] = (out[o.status] ?? 0) + 1;
    return out;
  }, [orders]);

  const supplierNames = useMemo(
    () => [...new Set(orders.map((o) => o.supplierName).filter((s): s is string => !!s))].sort(),
    [orders],
  );

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return orders.filter((o) => {
      if (o.status !== tab) return false;
      if (supplierFilter && o.supplierName !== supplierFilter) return false;
      if (!q) return true;
      return `${o.number ?? ''} ${o.supplierName ?? ''} ${o.lines
        .map((l) => l.itemName)
        .join(' ')}`
        .toLowerCase()
        .includes(q);
    });
  }, [orders, tab, supplierFilter, query]);

  const picked = orders.find((o) => o.id === pickedId) ?? null;

  /* The primary is the verb THIS state wants, and nothing else. */
  const primary = (() => {
    if (!canWrite) return undefined;
    if (tab === 'draft' && picked?.status === 'draft' && picked.lines.length) {
      return {
        label: `Send draft ▸ ${firstName(picked.supplierName)}`,
        onClick: () => setSending(picked),
      };
    }
    if (tab === 'ordered' && picked?.status === 'ordered') {
      return {
        label: 'Mark confirmed',
        onClick: () =>
          act.confirm.mutate(picked.id, {
            onSuccess: () => toast.success(`${picked.number} confirmed — Receive opens pre-filled`),
          }),
      };
    }
    if (tab === 'confirmed' && picked?.status === 'confirmed') {
      return {
        label: 'Receive → pre-filled',
        onClick: () => go(`/stock-manager/receive?order=${picked.id}`),
      };
    }
    if (tab === 'received' && picked?.status === 'received') {
      return {
        label: `Return a line ▸ ${firstName(picked.supplierName)}`,
        onClick: () => setReturning(picked),
      };
    }
    return undefined;
  })();


  /* `g_k8`: every live order in one list, in the order the work happens —
     drafts to approve, then what is out, then what is coming, then what came. */
  const phoneOrders = useMemo(() => {
    const rank: Record<string, number> = { draft: 0, ordered: 1, confirmed: 2, received: 3 };
    return orders
      .filter((o) => o.status in rank)
      .sort((a, b) => rank[a.status] - rank[b.status]);
  }, [orders]);

  const tableRows: TableRow[] = rows.map((o) => ({
    id: o.id,
    cells: {
      order: (
        <div style={{ minWidth: 0 }}>
          <p className="t-strong">
            {o.number ? `#${o.number}` : 'Draft'} · {o.supplierName ?? 'No supplier'}
          </p>
          <p className="t-cap mut">
            {o.lines.length} line{o.lines.length === 1 ? '' : 's'}
            {o.status === 'draft'
              ? ` · ${o.lines.map((l) => `${shortName(l.itemName)} ×${l.quantity}`).join(' · ')}`
              : o.status === 'received'
                ? ` · ${o.lines.length} lot${o.lines.length === 1 ? '' : 's'} on the shelf`
                : ` · ${orderMoney(o.totalMinor)}`}
          </p>
        </div>
      ),
      when: (
        <span className="mut">
          {o.status === 'draft'
            ? `${o.createdAt ? dayMonth(o.createdAt) : 'today'} · by ${o.raisedBy === 'maie' ? 'MAIE' : 'hand'}`
            : o.status === 'received'
              ? dayMonth(o.receivedAt)
              : dayMonth(o.sentAt)}
        </span>
      ),
      state: <StatusChip order={o} />,
      note: (
        <span className="t-sec mut" style={{ whiteSpace: 'nowrap' }}>
          {o.status === 'ordered' && o.sentVia
            ? `${CHANNEL_LABEL[o.sentVia]} ✓ · ${o.ageingDays} d`
            : o.status === 'confirmed'
              ? 'Receive opens pre-filled'
              : ''}
        </span>
      ),
      act: canWrite && o.status === 'draft' ? <DChip onClick={() => setPicked(o.id)}>Edit lines</DChip> : null,
    },
  }));

  useShellChrome(
    {
      raw: true,
      /* Phone: the pick is its own screen; back clears it. */
      paneOpen: !!picked,
      onPaneClose: () => setPicked(null),
      paneMenu: (close) => <StockMenu door="orders" onClose={close} />,
      /* `g_k8` — the desk table as cards. The rows are the table's
         own, so a figure or a state can never read differently here. */
      phone: (
        <PhoneScreen
          className="stock-phone"
          crumb="Stock"
          title="Orders"
          onBack={() => go('/stock-manager/shelf')}
          menu={(close) => <StockMenu door="orders" onClose={close} />}
        >
          {phoneOrders.length === 0 ? (
            <EmptyState title="No orders yet" sub="MAIE assembles one when the shelf runs down — you approve it before it is sent." />
          ) : (
            phoneOrders.map((o) => {
              const draft = o.status === 'draft';
              const tone =
                draft ? { borderColor: 'var(--brand-300)' }
                : o.status === 'confirmed' ? { borderColor: 'var(--success-600)' }
                : o.status === 'received' ? { opacity: 0.6 }
                : undefined;
              return (
                <div key={o.id} className="card" style={tone}>
                  <div className="flex" style={{ gap: 10, alignItems: 'flex-start' }}>
                    <div className="grow" style={{ minWidth: 0 }}>
                      <p className="t-strong">
                        {draft ? `Draft — ${o.raisedBy === 'maie' ? 'assembled by MAIE' : 'by hand'}` : `#${o.number} · ${o.supplierName ?? 'No supplier'}`}
                      </p>
                      <p className="t-cap mut mt4">
                        {draft
                          ? `${o.supplierName ?? 'No supplier'} · ${o.lines.length} item${o.lines.length === 1 ? '' : 's'}`
                          : o.status === 'ordered'
                            ? `sent ${dayMonth(o.sentAt)}${o.sentVia ? ` · ${CHANNEL_LABEL[o.sentVia]} ✓` : ''}`
                            : o.status === 'confirmed'
                              ? 'delivered · checked against the order ✓'
                              : `${dayMonth(o.receivedAt)} · ${o.lines.length} lot${o.lines.length === 1 ? '' : 's'} on the shelf`}
                      </p>
                    </div>
                    {draft && canWrite && o.lines.length ? (
                      <Chip kind="brand" small className="chip-act w6" onClick={() => setSending(o)}>Send ▾</Chip>
                    ) : (
                      <StatusChip order={o} />
                    )}
                  </div>
                  {draft ? (
                    <p className="t-sec mt8">
                      {o.lines.map((l) => `${shortName(l.itemName)} × ${l.quantity}`).join(' · ')}
                    </p>
                  ) : null}
                  {canWrite ? (
                    <div className="flex mt8" style={{ gap: 8, flexWrap: 'wrap' }}>
                      {draft ? (
                        <>
                          <DChip onClick={() => setPicked(o.id)}>Edit lines</DChip>
                          <DChip onClick={() => act.dismiss.mutate(o.id, { onSuccess: () => toast.success('Draft dismissed — nothing was ordered') })}>✕ Dismiss</DChip>
                        </>
                      ) : o.status === 'ordered' ? (
                        <DChip onClick={() => act.confirm.mutate(o.id, { onSuccess: () => toast.success(`${o.number} confirmed`) })}>Mark confirmed</DChip>
                      ) : o.status === 'confirmed' ? (
                        <DChip kind="brand" onClick={() => go(`/stock-manager/receive?order=${o.id}`)}>Receive → pre-filled</DChip>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            })
          )}
        </PhoneScreen>
      ),
      title: 'Orders',
      sub: isLoading ? 'reading the orders…' : 'Draft → Ordered → Confirmed → Received',
      search: 'Order number, supplier or item',
      searchValue: query,
      onSearchChange: setQuery,
      secondary: { label: 'Suppliers', onClick: () => go('/stock-manager/catalog') },
      ...(primary ? { primary } : {}),
      pane: picked ? (
        <OrderPane
          order={picked}
          canWrite={canWrite}
          onClose={() => setPicked(null)}
          onEditLine={setEditing}
          onSend={() => setSending(picked)}
          onConfirm={() =>
            act.confirm.mutate(picked.id, {
              onSuccess: () => toast.success(`${picked.number} confirmed`),
            })
          }
          onReceive={(check) => go(`/stock-manager/receive?order=${picked.id}${check ?? ''}`)}
          onDismiss={() =>
            act.dismiss.mutate(picked.id, {
              onSuccess: () => {
                toast.success('Draft dismissed — nothing was ordered');
                setPicked(null);
              },
            })
          }
          onOpenShelf={() => go('/stock-manager/shelf')}
          onReturn={() => setReturning(picked)}
          onNudge={() =>
            act.nudge.mutate(picked.id, {
              onSuccess: () => toast.success(`Nudged ${picked.supplierName ?? 'the supplier'} on WhatsApp`),
              onError: (e) => toast.error(apiMessage(e, 'The nudge could not be sent.')),
            })
          }
          onAutoSend={(on) =>
            act.autoSend.mutate(
              { orderId: picked.id, on },
              {
                onSuccess: () =>
                  toast.success(on ? `Auto-send is on for ${picked.supplierName}` : 'Auto-send is off'),
                onError: (e) => toast.error(apiMessage(e, 'The switch could not be changed.')),
              },
            )
          }
        />
      ) : (
        <Pane
          head={
            <PaneHead
              overline={tab === 'draft' ? 'Draft' : tab}
              title="Nothing picked"
              sub={PANE_HINT[tab]}
            />
          }
        >
          <div className="emptystate">
            <p className="t-sec">{PANE_BODY[tab]}</p>
          </div>
        </Pane>
      ),
    },
    [picked, tab, canWrite, query, isLoading, primary?.label, pickedId, phoneOrders],
  );

  const doReturn = (itemId: string, quantity: number, note: string) =>
    giveBack.mutate(
      { itemId, quantity, note, reason: 'return' },
      {
        onSuccess: () => {
          toast.success('Returned — the stock left as awaiting credit');
          setReturning(null);
        },
        onError: (e) => toast.error(apiMessage(e, 'The return could not be recorded.')),
      },
    );

  return (
    <div className="maie desk stock-root">
      <ReturnModal order={returning} onClose={() => setReturning(null)} onReturn={doReturn} />
      {supplierNames.length > 1 ? (
        <div className="filterrow">
          <Chip kind={supplierFilter ? 'outline' : 'brand'} small onClick={() => setSupplier('')}>
            All suppliers{supplierFilter ? '' : ' ✓'}
          </Chip>
          {supplierNames.map((n) => (
            <Chip
              key={n}
              kind={supplierFilter === n ? 'brand' : 'outline'}
              small
              onClick={() => setSupplier(supplierFilter === n ? '' : n)}
            >
              {n}
            </Chip>
          ))}
        </div>
      ) : null}

      <div className="dcontent scroll">
        {/* IN THE CONTENT COLUMN, not the filter row: the kit gives each tab
            `flex:1`, which only divides a full-width strip — inside a
            content-sized row the four of them shrink onto two lines. */}
        <TabBar
          label="Orders"
          items={TABS.map((t) => ({
            key: t.key,
            label: counts[t.key] ? `${t.label} · ${counts[t.key]}` : t.label,
          }))}
          active={tab}
          onSelect={(k) => {
            setTab(k);
            setPicked(null);
          }}
        />
        <DataTable
          caption={`Orders — ${tab}`}
          columns={[
            { key: 'order', label: 'Order', width: 40 },
            { key: 'when', label: 'When', width: 15 },
            { key: 'state', label: 'State', width: 12 },
            /* "WhatsApp ✓ · 4 d" is one phrase; wrapped over two lines it
               stops reading as one. */
            { key: 'note', label: '', width: 21 },
            { key: 'act', label: '', width: 10 },
          ]}
          rows={tableRows}
          selectedId={pickedId}
          onSelect={(r) => setPicked(r.id === pickedId ? null : r.id)}
          empty={
            <div className="emptystate">
              {isLoading ? (
                <p className="t-sec mut">Reading the orders…</p>
              ) : isError ? (
                <p className="t-sec mut">The orders did not come back. Nothing has been changed.</p>
              ) : (
                <>
                  <p className="es-t">{EMPTY_TITLE[tab]}</p>
                  <p className="t-sec mut">{EMPTY_BODY[tab]}</p>
                  {tab === 'draft' && canWrite && suppliers?.length ? (
                    <p className="mt12">
                      <PickerChip
                        label="Raise one by hand ▾"
                        ariaLabel="Raise a draft"
                        kind="brand"
                        value=""
                        onChange={(id) =>
                          id &&
                          act.createDraft.mutate(id, {
                            onSuccess: (o) => {
                              setPicked(o.id);
                              toast.success('Empty draft raised — add what you need');
                            },
                          })
                        }
                        options={[
                          { value: '', label: 'Which supplier?' },
                          ...suppliers.map((s) => ({ value: s.id, label: s.name })),
                        ]}
                      />
                    </p>
                  ) : null}
                </>
              )}
            </div>
          }
        />

        <p className="t-sec mut mt16">{FOOTNOTE[tab]}</p>
      </div>

      <SendModal
        order={sending}
        onClose={() => setSending(null)}
        onSend={(via) => {
          if (!sending) return;
          act.send.mutate(
            { orderId: sending.id, via },
            {
              onSuccess: (o) =>
                toast.success(`#${o.number} sent to ${sending.supplierName ?? 'the supplier'}`),
              onError: () => toast.error('It was not sent. Nothing has been changed.'),
            },
          );
          setSending(null);
          setTab('ordered');
        }}
      />
      <QuantityModal
        line={editing}
        onClose={() => setEditing(null)}
        onSave={(quantity) => {
          if (!editing) return;
          act.setQuantity.mutate(
            { lineId: editing.id, quantity },
            {
              onSuccess: () =>
                toast.success(
                  quantity === 0
                    ? `${editing.itemName} taken off the draft`
                    : `${editing.itemName} × ${quantity}`,
                ),
            },
          );
          setEditing(null);
        }}
      />
    </div>
  );
}

const PANE_HINT: Record<OrderStatus, string> = {
  draft: 'pick a draft to read its case, line by line',
  ordered: 'pick an order to see how long it has been waiting',
  confirmed: 'pick an order the supplier has confirmed',
  received: 'pick a closed order to see what it put on the shelf',
  cancelled: 'pick a dismissed draft',
};

const PANE_BODY: Record<OrderStatus, string> = {
  draft:
    'MAIE assembles; you approve. Every line carries the argument that raised it — a minimum it fell under, or a dated lot that will not survive the month.',
  ordered:
    'An order that has been sent is money promised. It ages here until the supplier confirms it, because an order nobody confirmed is the one that never arrives.',
  confirmed: 'The supplier says it is coming, so Receive can open pre-filled — nothing typed twice.',
  received: 'A received order is closed. What was wrong with it goes back as a return, never as an edit.',
  cancelled: 'Dismissed drafts are kept — the case they made is history, not a mistake to erase.',
};

const EMPTY_TITLE: Record<OrderStatus, string> = {
  draft: 'Nothing needs ordering',
  ordered: 'Nothing is on order',
  confirmed: 'Nothing is confirmed',
  received: 'Nothing has been received yet',
  cancelled: 'No dismissed drafts',
};

const EMPTY_BODY: Record<OrderStatus, string> = {
  draft:
    'Every item with auto-order on is above its threshold, and nothing is expiring that the surviving stock will not cover. Drafts appear here the moment that changes.',
  ordered: 'Sending a draft puts it here, where it ages until the supplier confirms.',
  confirmed: 'Marking an order confirmed puts it here, and opens Receive pre-filled from its lines.',
  received: 'An order closes here once every line of it has arrived on the shelf.',
  cancelled: 'A draft you dismiss is kept here rather than deleted.',
};

const FOOTNOTE: Record<OrderStatus, string> = {
  draft:
    'A min-level breach drops the item into its preferred supplier’s draft, quantity suggested from real consumption; the holder edits and sends it.',
  ordered: 'Ordered ages visibly, like an unpaid claim.',
  confirmed: 'Confirming opens Receive PRE-FILLED — nothing typed twice.',
  received:
    'A received order stops ageing. Anything wrong with it now goes back as a return, never as an edit — the lots are already on the shelf.',
  cancelled: 'Dismissed, not deleted.',
};

/** "El Nile Medical" → "El Nile", for a button that has to fit. */
const firstName = (name: string | null) =>
  (name ?? 'the supplier').split(' ').slice(0, 2).join(' ');

/** Item names are long; a draft row lists several. */
const shortName = (name: string) => (name.length > 22 ? `${name.slice(0, 20)}…` : name);

function StatusChip({ order }: { order: Order }) {
  if (order.status === 'draft') return <Chip kind="soft" small>Draft</Chip>;
  if (order.status === 'ordered') {
    /* AGEING IS THE POINT OF THIS STATE. Past a working week without a
       confirmation the chip stops being neutral. */
    const stale = (order.ageingDays ?? 0) >= 5;
    return <Chip kind={stale ? 'warn' : 'soft'} small>Ordered</Chip>;
  }
  if (order.status === 'confirmed') return <Chip kind="success" small>Confirmed</Chip>;
  if (order.status === 'received') return <Chip kind="success" small>Received ✓</Chip>;
  return <Chip kind="outline" small>Dismissed</Chip>;
}

/** The docked order — a different pane per state, because a different question. */
function OrderPane({
  order,
  canWrite,
  onClose,
  onEditLine,
  onSend,
  onConfirm,
  onReceive,
  onDismiss,
  onOpenShelf,
  onNudge,
  onAutoSend,
  onReturn,
}: {
  order: Order;
  canWrite: boolean;
  onClose: () => void;
  onEditLine: (line: OrderLine) => void;
  onSend: () => void;
  onConfirm: () => void;
  onReceive: (check?: string) => void;
  onDismiss: () => void;
  onOpenShelf: () => void;
  onNudge: () => void;
  onAutoSend: (on: boolean) => void;
  onReturn: () => void;
}) {
  const narrow = useNarrowViewport();
  /* The switch writes the supplier's Directory row — a manager's. */
  const canManage = useAuthStore((st) => st.roleCheck.hasPermission(Permission.SYSTEM_WRITE) || st.roleCheck.hasPermission(Permission.HOSPITAL_SETTINGS_WRITE));
  /* `g_ord_conf` — the delivery check: what was ordered against what the box
     actually holds. Short lines need a word before Receive opens. */
  const [got, setGot] = useState<Record<string, string>>({});
  const [shortNote, setShortNote] = useState('');
  const outstanding = (l: OrderLine) => Math.max(0, l.quantity - l.receivedQuantity);
  const deliveredOf = (l: OrderLine) => (got[l.id] === undefined ? outstanding(l) : Math.max(0, Number(got[l.id]) || 0));
  const shortBy = order.lines.reduce((n, l) => n + Math.max(0, outstanding(l) - deliveredOf(l)), 0);
  const checkQuery = () =>
    `&d=${order.lines.map((l) => `${l.id}:${deliveredOf(l)}`).join(',')}${shortBy ? `&n=${encodeURIComponent(shortNote.trim())}` : ''}`;
  const overline =
    order.status === 'draft'
      ? order.raisedBy === 'maie'
        ? 'Draft · assembled by MAIE'
        : 'Draft · raised by hand'
      : order.status === 'ordered'
        ? 'Ordered · ageing'
        : order.status === 'confirmed'
          ? canWrite ? 'Confirmed · delivery check' : 'Confirmed · coming'
          : 'Received · closed';

  const sub =
    order.status === 'draft'
      ? `${order.lines.length} line${order.lines.length === 1 ? '' : 's'} · not sent · nothing has moved`
      : order.status === 'ordered'
        ? `sent ${dayMonth(order.sentAt)} · no confirmation yet`
        : order.status === 'confirmed'
          ? `sent ${dayMonth(order.sentAt)} · confirmed ${dayMonth(order.confirmedAt)}`
          : `placed ${dayMonth(order.sentAt)} · ${order.lines.length} lot${order.lines.length === 1 ? '' : 's'} on the shelf`;

  return (
    <Pane
      onClose={onClose}
      head={
        <PaneHead
          overline={overline}
          title={order.number ? `#${order.number} · ${order.supplierName ?? ''}` : (order.supplierName ?? 'No supplier')}
          sub={sub}
          wrapTitle
        />
      }
      foot={
        canWrite ? (
          order.status === 'draft' ? (
            <>
              <button type="button" className="btn btn-outline" style={{ flex: 1, minHeight: 44 }} onClick={onDismiss}>
                ✕ Dismiss
              </button>
              <button
                type="button"
                className="btn btn-primary"
                style={{ flex: 1, minHeight: 44 }}
                disabled={!order.lines.length}
                onClick={onSend}
              >
                Send ▸ {firstName(order.supplierName)}
              </button>
            </>
          ) : order.status === 'ordered' ? (
            <>
              <button type="button" className="btn btn-outline" style={{ flex: 1, minHeight: 44 }} onClick={onConfirm}>
                Mark confirmed
              </button>
              <button type="button" className="btn btn-primary" style={{ flex: 1, minHeight: 44 }} onClick={onNudge}>
                Nudge ▸ WhatsApp
              </button>
            </>
          ) : order.status === 'confirmed' ? (
            <button type="button" className="btn btn-primary grow" style={{ minHeight: 44 }}
              disabled={shortBy > 0 && shortNote.trim().length < 3}
              onClick={() => onReceive(checkQuery())}>
              Confirm → open Receive
            </button>
          ) : (
            <>
              {/* Desk: the return is the header's primary (g_ord_rec). */}
              {narrow ? (
                <button type="button" className="btn btn-outline" style={{ flex: 1, minHeight: 44 }} onClick={onReturn}>
                  Return a line ▸
                </button>
              ) : null}
              <button type="button" className="btn btn-outline grow" style={{ minHeight: 44 }} onClick={onOpenShelf}>
                Open the shelf ▸
              </button>
            </>
          )
        ) : undefined
      }
    >
      {order.status !== 'draft' ? (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <div className="card" style={{ flex: 1, margin: 0 }}>
            <p className="t-ovl mut">Lines</p>
            <p className="t-heading mt8">{order.lines.length}</p>
          </div>
          <div className="card" style={{ flex: 1, margin: 0 }}>
            <p className="t-ovl mut">Value</p>
            <p className="t-heading mt8">
              {order.totalMinor ? orderMoney(order.totalMinor).replace('EGP ', '') : '—'}
            </p>
          </div>
          {order.status === 'ordered' ? (
            <div className="card" style={{ flex: 1, margin: 0 }}>
              <p className="t-ovl mut">Age</p>
              <p className={`t-heading mt8${(order.ageingDays ?? 0) >= 5 ? ' amb' : ''}`}>
                {order.ageingDays} d
              </p>
            </div>
          ) : null}
        </div>
      ) : null}

      {order.status === 'confirmed' && canWrite ? (
        <>
          <p className="t-ovl mut" style={{ margin: '14px 0 6px' }}>Delivery check — ordered against delivered</p>
          {order.lines.map((line) => {
            const short = Math.max(0, outstanding(line) - deliveredOf(line));
            return (
              <Card key={line.id} style={short ? { borderColor: 'var(--warning-600)' } : undefined}>
                <p className="t-strong">{line.itemName}</p>
                <div className="flex mt8" style={{ gap: 12, alignItems: 'center' }}>
                  <p className="t-sec mut grow">ordered {outstanding(line)}</p>
                  <label className="t-sec mut" htmlFor={`dc-${line.id}`}>delivered</label>
                  <input id={`dc-${line.id}`} className="fld t-strong num" style={{ width: 64 }} type="number" min={0}
                    inputMode="numeric" value={got[line.id] ?? String(outstanding(line))}
                    onChange={(e) => setGot((g) => ({ ...g, [line.id]: e.target.value }))} />
                </div>
                {short ? <p className="t-cap amb mt8">Short {short}</p> : null}
              </Card>
            );
          })}
          {shortBy ? (
            <Card style={{ borderColor: 'var(--warning-600)' }}>
              <p className="t-sec amb">Short {shortBy} — note required</p>
              <input className="fld t-sec mt8" aria-label="Note on the shortfall"
                placeholder="what the supplier said" value={shortNote}
                onChange={(e) => setShortNote(e.target.value)} />
            </Card>
          ) : (
            <p className="t-cap mut">Everything ordered arrived — Receive opens pre-filled with these quantities.</p>
          )}
        </>
      ) : null}

      {order.status === 'confirmed' && canWrite ? null : (
        <p className="t-ovl mut" style={{ margin: '14px 0 6px' }}>
          {order.status === 'received' ? 'What arrived' : 'Lines'}
        </p>
      )}
      {order.status === 'confirmed' && canWrite ? null : order.lines.length === 0 ? (
        <Card>
          <p className="t-sec mut">
            Nothing on this draft yet — an order with no lines asks for nothing.
          </p>
        </Card>
      ) : (
        order.lines.map((line) => (
          <Card key={line.id}>
            <div className="flex" style={{ gap: 8, alignItems: 'flex-start' }}>
              <p className="t-ovl mut grow">{line.itemName}</p>
              {order.status === 'received' && line.receivedQuantity < line.quantity ? (
                <Chip kind="warn" small>part</Chip>
              ) : null}
            </div>
            <p className="t-strong mt8">
              × {line.quantity}
              {order.status !== 'draft' && order.status !== 'ordered'
                ? ` · ${line.receivedQuantity} received`
                : ''}
              {canWrite && order.status === 'draft' ? (
                <>
                  {' '}
                  <button
                    type="button"
                    className="lnk w6 brand"
                    style={{ background: 'none', border: 0, padding: 0, font: 'inherit', cursor: 'pointer' }}
                    onClick={() => onEditLine(line)}
                  >
                    edit
                  </button>
                </>
              ) : null}
            </p>
            {line.reasonNote ? <p className="t-cap mut mt8">{line.reasonNote}</p> : null}
          </Card>
        ))
      )}

      <p className="t-ovl mut" style={{ margin: '14px 0 6px' }}>Supplier</p>
      <Card>
        <p className="t-strong">{order.supplierName ?? 'Not named'}</p>
        {order.supplierContact ? (
          <p className="t-cap mut mt8">{order.supplierContact}</p>
        ) : (
          <p className="t-cap mut mt8">
            no contact on file — the Directory owns that record
          </p>
        )}
      </Card>

      {order.status === 'ordered' ? (
        <Card style={{ borderColor: 'var(--warning-600)' }}>
          <p className="t-sec" style={{ color: 'var(--warning-600)' }}>
            {order.ageingDays === 0
              ? 'Sent today. Confirming opens Receive PRE-FILLED — nothing typed twice.'
              : `${order.ageingDays} day${order.ageingDays === 1 ? '' : 's'} without a confirmation. Confirming opens Receive PRE-FILLED — nothing typed twice.`}
          </p>
          <p className="t-cap mut mt8">
            {order.sentVia ? `asked by ${CHANNEL_LABEL[order.sentVia]} on ${dayMonth(order.sentAt)}` : ''}
          </p>
        </Card>
      ) : null}

      {order.status === 'draft' ? (
        <Card kind={order.supplierAutoSend ? 'accent' : undefined}>
          <div className="flex" style={{ gap: 8, alignItems: 'center' }}>
            <div className="grow">
              <p className="t-ovl mut">Auto-send to {order.supplierName ?? 'this supplier'}</p>
              <p className="t-strong mt8">{order.supplierAutoSend ? 'On' : 'Off'}</p>
            </div>
            {canWrite && canManage && order.supplierId ? (
              <button
                type="button"
                className="dchip dchip-outline"
                onClick={() => onAutoSend(!order.supplierAutoSend)}
              >
                {order.supplierAutoSend ? 'Turn off' : 'Turn on'}
              </button>
            ) : null}
          </div>
          <p className="t-cap mut mt8">
            {order.supplierAutoSend
              ? 'drafts for this supplier go out on WhatsApp by themselves when the shelf raises them'
              : 'off by default — reordering is hands-free only when you say so'}
          </p>
        </Card>
      ) : null}

      {order.status === 'received' ? (
        <Card kind="accent">
          <p className="t-sec brand">
            A return is not a write-off: the stock leaves as “returned — awaiting credit”, and the
            credit note nets against the supplier’s next invoice.
          </p>
        </Card>
      ) : null}
    </Pane>
  );
}

/** The server's own sentence, when it gave one. */
function apiMessage(e: unknown, fallback: string): string {
  const m = (e as { response?: { data?: { error?: { message?: unknown } } } })?.response?.data?.error?.message;
  return typeof m === 'string' ? m : fallback;
}

/** `g_ord_rec` — Return a line: a real movement to the supplier, not a write-off. */
function ReturnModal({
  order,
  onClose,
  onReturn,
}: {
  order: Order | null;
  onClose: () => void;
  onReturn: (itemId: string, quantity: number, note: string) => void;
}) {
  const [lineId, setLineId] = useState('');
  const [qty, setQty] = useState('1');
  const [note, setNote] = useState('');
  const lines = order?.lines ?? [];
  const line = lines.find((l) => l.id === lineId) ?? lines[0];
  const max = line?.receivedQuantity || line?.quantity || 1;
  const amount = Math.min(max, Math.max(1, Number(qty) || 1));
  const ready = !!line && note.trim().length > 2;
  return (
    <Modal
      open={!!order}
      onClose={onClose}
      title="Return a line"
      sub={order ? `${order.number ?? 'Order'} · ${order.supplierName ?? 'the supplier'}` : undefined}
      foot={
        <>
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!ready}
            onClick={() => line && onReturn(line.itemId, amount, note.trim())}
          >
            Return {amount}
          </button>
        </>
      }
    >
      {order ? (
        <>
          <p className="t-ovl mut">Which line</p>
          <p className="mt4">
            <PickerChip
              label={`${line?.itemName ?? 'Pick a line'} ▾`}
              ariaLabel="Line to return"
              value={line?.id ?? ''}
              options={lines.map((l) => ({ value: l.id, label: `${l.itemName} × ${l.receivedQuantity || l.quantity}` }))}
              onChange={setLineId}
            />
          </p>
          <label className="t-ovl mut mt12" htmlFor="rt-qty">How many</label>
          <input id="rt-qty" className="fld mt4" type="number" min={1} max={max} value={qty} onChange={(e) => setQty(e.target.value)} />
          <label className="t-ovl mut mt12" htmlFor="rt-note">Why</label>
          <input id="rt-note" className="fld mt4" placeholder="damaged · wrong item · recalled" onChange={(e) => setNote(e.target.value)} />
          <p className="t-cap mut mt8">
            The stock leaves as “returned — awaiting credit”, and the credit note nets against the supplier’s next invoice.
          </p>
        </>
      ) : null}
    </Modal>
  );
}

/** How the supplier was actually asked — which is what a nudge follows up on. */
function SendModal({
  order,
  onClose,
  onSend,
}: {
  order: Order | null;
  onClose: () => void;
  onSend: (via: OrderChannel) => void;
}) {
  const [via, setVia] = useState<OrderChannel>('whatsapp');

  return (
    <Modal
      open={!!order}
      onClose={onClose}
      title={order ? `Send to ${order.supplierName ?? 'the supplier'}?` : 'Send'}
      sub="it stops being a suggestion, and gets a number"
      foot={
        <>
          <button type="button" className="btn btn-outline" onClick={onClose}>Not yet</button>
          <button type="button" className="btn btn-primary" onClick={() => onSend(via)}>
            Send it
          </button>
        </>
      }
    >
      {order ? (
        <>
          {order.lines.map((l) => (
            <p key={l.id} className="t-sec sec mt4">
              {l.itemName} × {l.quantity}
            </p>
          ))}
          <p className="t-strong mt12">{orderMoney(order.totalMinor)}</p>
          <p className="t-ovl mut mt12">How you asked them</p>
          <p className="mt4">
            <PickerChip
              label={`${CHANNEL_LABEL[via]} ▾`}
              ariaLabel="Channel"
              kind="brand"
              value={via}
              onChange={setVia}
              options={CHANNELS}
            />
          </p>
          <p className="t-cap mut mt8">
            MAIE does not message the supplier for you — you send it the way you always do, and this
            records which way, so the order can be chased later.
          </p>
        </>
      ) : null}
    </Modal>
  );
}

/** Editing a quantity makes the line the holder's, and the pane says so. */
function QuantityModal({
  line,
  onClose,
  onSave,
}: {
  line: OrderLine | null;
  onClose: () => void;
  onSave: (quantity: number) => void;
}) {
  const [value, setValue] = useState('');

  return (
    <Modal
      open={!!line}
      onClose={onClose}
      title={line ? line.itemName : 'How many'}
      sub={line?.reasonNote ?? undefined}
      foot={
        <>
          <button
            type="button"
            className="btn btn-outline"
            onClick={() => {
              onSave(0);
              setValue('');
            }}
          >
            ✕ Take it off
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              onSave(Math.max(1, Number(value) || line?.quantity || 1));
              setValue('');
            }}
          >
            Save
          </button>
        </>
      }
    >
      {line ? (
        <>
          <label className="t-ovl mut" htmlFor="ord-qty">How many</label>
          <input
            id="ord-qty"
            className="fld t-strong mt4 num"
            type="number"
            min={1}
            inputMode="numeric"
            defaultValue={line.quantity}
            onChange={(e) => setValue(e.target.value)}
          />
          <p className="t-cap mut mt8">
            Setting it by hand makes the line yours — the draft will stop arguing MAIE’s number for
            it, and the next assembly will leave it alone.
          </p>
        </>
      ) : null}
    </Modal>
  );
}
